import React, { useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  ButtonGroup,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Rating,
  useTheme,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Download,
  Print,
  CalendarMonth,
  DirectionsCar,
  Person,
  AttachMoney,
  Star,
  Schedule,
  CheckCircle,
  Cancel,
  Warning,
  Refresh,
  FilterList,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

export const AnalyticsPage: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState('last7days');
  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [selectedMetric, setSelectedMetric] = useState('all');

  // Sample data for charts
  const revenueData = [
    { date: 'Mon', revenue: 4200, bookings: 28, avgValue: 150 },
    { date: 'Tue', revenue: 5100, bookings: 34, avgValue: 150 },
    { date: 'Wed', revenue: 4800, bookings: 32, avgValue: 150 },
    { date: 'Thu', revenue: 6200, bookings: 41, avgValue: 151 },
    { date: 'Fri', revenue: 7500, bookings: 48, avgValue: 156 },
    { date: 'Sat', revenue: 8900, bookings: 56, avgValue: 159 },
    { date: 'Sun', revenue: 8100, bookings: 52, avgValue: 156 },
  ];

  const vehiclePerformance = [
    { name: 'SUVs', value: 35, revenue: 125000, utilization: 78 },
    { name: 'Sedans', value: 28, revenue: 84000, utilization: 72 },
    { name: 'Luxury', value: 18, revenue: 108000, utilization: 85 },
    { name: 'Economy', value: 12, revenue: 36000, utilization: 65 },
    { name: 'Vans', value: 7, revenue: 42000, utilization: 70 },
  ];

  const customerSatisfaction = [
    { rating: 5, count: 234, percentage: 45 },
    { rating: 4, count: 178, percentage: 34 },
    { rating: 3, count: 67, percentage: 13 },
    { rating: 2, count: 31, percentage: 6 },
    { rating: 1, count: 10, percentage: 2 },
  ];

  const topDrivers = [
    { id: 1, name: 'John Smith', avatar: '/api/placeholder/40/40', trips: 156, rating: 4.9, revenue: 8500, status: 'active' },
    { id: 2, name: 'Sarah Johnson', avatar: '/api/placeholder/40/40', trips: 143, rating: 4.8, revenue: 7800, status: 'active' },
    { id: 3, name: 'Mike Davis', avatar: '/api/placeholder/40/40', trips: 128, rating: 4.7, revenue: 6900, status: 'active' },
    { id: 4, name: 'Emma Wilson', avatar: '/api/placeholder/40/40', trips: 119, rating: 4.9, revenue: 6500, status: 'active' },
    { id: 5, name: 'Tom Brown', avatar: '/api/placeholder/40/40', trips: 112, rating: 4.6, revenue: 6100, status: 'active' },
  ];

  const bookingStatus = [
    { status: 'Completed', value: 68, color: theme.palette.success.main },
    { status: 'Active', value: 18, color: theme.palette.info.main },
    { status: 'Cancelled', value: 8, color: theme.palette.error.main },
    { status: 'Pending', value: 6, color: theme.palette.warning.main },
  ];

  const COLORS = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main, theme.palette.warning.main, theme.palette.error.main];

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    const today = new Date();
    
    switch (value) {
      case 'today':
        setStartDate(today);
        setEndDate(today);
        break;
      case 'yesterday':
        const yesterday = subDays(today, 1);
        setStartDate(yesterday);
        setEndDate(yesterday);
        break;
      case 'last7days':
        setStartDate(subDays(today, 7));
        setEndDate(today);
        break;
      case 'thisWeek':
        setStartDate(startOfWeek(today));
        setEndDate(endOfWeek(today));
        break;
      case 'thisMonth':
        setStartDate(startOfMonth(today));
        setEndDate(endOfMonth(today));
        break;
      case 'custom':
        // Keep current dates for custom range
        break;
    }
  };

  const StatCard = ({ title, value, change, icon, color }: any) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              {value}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {change > 0 ? (
                <TrendingUp sx={{ fontSize: 20, color: theme.palette.success.main }} />
              ) : (
                <TrendingDown sx={{ fontSize: 20, color: theme.palette.error.main }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: change > 0 ? theme.palette.success.main : theme.palette.error.main,
                  fontWeight: 500,
                }}
              >
                {Math.abs(change)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                vs last period
              </Typography>
            </Box>
          </Box>
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Analytics & Reports
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive insights into your business performance
          </Typography>
        </Box>

        {/* Date Range Selector */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => handleDateRangeChange(e.target.value)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="thisWeek">This Week</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
            
            {dateRange === 'custom' && (
              <>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh Data">
              <IconButton 
                color="primary"
                onClick={() => {
                  // Simulate data refresh
                  alert('Analytics data refreshed successfully!');
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Report">
              <IconButton 
                color="primary"
                onClick={() => {
                  // Generate and download analytics report
                  const reportData = {
                    totalRevenue: '$45,200',
                    totalBookings: '283',
                    activeVehicles: '156',
                    totalCustomers: '1,249',
                    period: 'Current Month',
                    generated: new Date().toLocaleString()
                  };
                  
                  const reportContent = `
ANALYTICS REPORT
================

Period: ${reportData.period}
Total Revenue: ${reportData.totalRevenue}
Total Bookings: ${reportData.totalBookings}
Active Vehicles: ${reportData.activeVehicles}
Total Customers: ${reportData.totalCustomers}

Generated: ${reportData.generated}
`;
                  
                  const blob = new Blob([reportContent], { type: 'text/plain' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `analytics_report_${new Date().toISOString().split('T')[0]}.txt`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                }}
              >
                <Download />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print Report">
              <IconButton 
                color="primary"
                onClick={() => {
                  window.print();
                }}
              >
                <Print />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Overview" />
            <Tab label="Revenue" />
            <Tab label="Vehicles" />
            <Tab label="Customers" />
            <Tab label="Drivers" />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* Key Metrics */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Revenue"
                value="$45,200"
                change={12.5}
                icon={<AttachMoney />}
                color={theme.palette.primary.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Bookings"
                value="283"
                change={8.3}
                icon={<DirectionsCar />}
                color={theme.palette.secondary.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Active Users"
                value="1,847"
                change={15.2}
                icon={<Person />}
                color={theme.palette.success.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Avg Rating"
                value="4.6"
                change={-2.1}
                icon={<Star />}
                color={theme.palette.warning.main}
              />
            </Grid>
          </Grid>

          {/* Charts Row 1 */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Revenue & Bookings Trend
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <RechartsTooltip />
                      <Legend />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="revenue"
                        stroke={theme.palette.primary.main}
                        fill={alpha(theme.palette.primary.main, 0.1)}
                        name="Revenue ($)"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="bookings"
                        stroke={theme.palette.secondary.main}
                        name="Bookings"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Booking Status
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={bookingStatus}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.status}: ${entry.value}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {bookingStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Performance Metrics */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Vehicle Category Performance
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={vehiclePerformance}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="utilization" fill={theme.palette.primary.main} name="Utilization %" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Customer Satisfaction
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Rating value={4.6} precision={0.1} readOnly />
                      <Typography variant="h4">4.6</Typography>
                      <Typography variant="body2" color="text.secondary">
                        (520 reviews)
                      </Typography>
                    </Box>
                  </Box>
                  {customerSatisfaction.map((item) => (
                    <Box key={item.rating} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{item.rating} stars</Typography>
                          <Typography variant="body2" color="text.secondary">
                            ({item.count})
                          </Typography>
                        </Box>
                        <Typography variant="body2">{item.percentage}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={item.percentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: theme.palette.primary.main,
                          },
                        }}
                      />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Revenue Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Revenue Analysis</Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Metric</InputLabel>
                      <Select
                        value={selectedMetric}
                        label="Metric"
                        onChange={(e) => setSelectedMetric(e.target.value)}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="bookings">Bookings</MenuItem>
                        <MenuItem value="carRentals">Car Rentals</MenuItem>
                        <MenuItem value="driverHires">Driver Hires</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" stroke={theme.palette.primary.main} name="Revenue" />
                      <Line type="monotone" dataKey="avgValue" stroke={theme.palette.secondary.main} name="Avg Booking Value" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Vehicles Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Fleet Performance
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Category</TableCell>
                          <TableCell align="right">Total Vehicles</TableCell>
                          <TableCell align="right">Active</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                          <TableCell align="right">Utilization</TableCell>
                          <TableCell align="right">Trend</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vehiclePerformance.map((row) => (
                          <TableRow key={row.name}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell align="right">{row.value}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={Math.floor(row.value * 0.8)}
                                size="small"
                                color="success"
                              />
                            </TableCell>
                            <TableCell align="right">${row.revenue.toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={row.utilization}
                                  sx={{ width: 60, height: 6 }}
                                />
                                <Typography variant="body2">{row.utilization}%</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <ArrowUpward sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Customers Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Customer Demographics
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Age Groups</Typography>
                    <Box sx={{ mt: 1 }}>
                      {[
                        { label: '18-25', value: 15 },
                        { label: '26-35', value: 35 },
                        { label: '36-45', value: 28 },
                        { label: '46-55', value: 15 },
                        { label: '55+', value: 7 },
                      ].map((item) => (
                        <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">{item.label}</Typography>
                          <Typography variant="body2" fontWeight={500}>{item.value}%</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Customer Retention
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart
                      data={[
                        { month: 'Jan', newCustomers: 120, returning: 280 },
                        { month: 'Feb', newCustomers: 150, returning: 300 },
                        { month: 'Mar', newCustomers: 180, returning: 320 },
                        { month: 'Apr', newCustomers: 140, returning: 350 },
                        { month: 'May', newCustomers: 190, returning: 380 },
                        { month: 'Jun', newCustomers: 210, returning: 400 },
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="returning"
                        stackId="1"
                        stroke={theme.palette.primary.main}
                        fill={theme.palette.primary.main}
                      />
                      <Area
                        type="monotone"
                        dataKey="newCustomers"
                        stackId="1"
                        stroke={theme.palette.secondary.main}
                        fill={theme.palette.secondary.main}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Drivers Tab */}
        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Top Performing Drivers
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Driver</TableCell>
                          <TableCell align="right">Total Trips</TableCell>
                          <TableCell align="right">Rating</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                          <TableCell align="right">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topDrivers.map((driver) => (
                          <TableRow key={driver.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar src={driver.avatar} alt={driver.name} />
                                <Typography variant="body2">{driver.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{driver.trips}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                                <Star sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                                <Typography variant="body2">{driver.rating}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">${driver.revenue.toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label="Active"
                                size="small"
                                color="success"
                                icon={<CheckCircle sx={{ fontSize: 16 }} />}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Box>
    </LocalizationProvider>
  );
};